import { Box, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IconHeader } from "../../../../Icon/IconHader/IconHeader";
import {
  parseDateToDDMMYYYY2,
  validInitDates,
} from "../../../../../helpers/dateTransform";
import { InvestigationBlockInfo } from "../components";
import { useEffect, useState } from "react";
import { detailAdvisorReview } from "../../../../../apis";
import Icon from "../../../../Icon/Icon";
import { useSelector } from "react-redux";

export const GeneralData = ({
  reportDetail,
  investigation,
  catalogCompanyRelationship,
  grievanceClassificationCat,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const [advisorReview, setAdvisorReview] = useState(null);

  useEffect(() => {
    detailAdvisorReview(reportDetail.id)
      .then((resp) => setAdvisorReview(resp.review ?? null))
      .catch((err) => console.log(err));
  }, []);

  return (
    <>
      <CapturedInformation reportDetail={reportDetail} />

      {advisorReview && (
        <>
          <IconHeader
            iconName="outline_user_status"
            title={t("General.advisor_comment")}
            uppercase={false}
          />

            <Stack
              my={1.5}
              spacing={1.5}
            >
              {
                Object.entries(advisorReview.questions_of_params ?? {}).map(([key, val]) => (
                  <Stack
                    key={key}
                    direction="row"
                    alignItems="center"
                    gap={1}
                  >
                    <Icon name="bold_tag_verify" color={theme.palette.primary.main} size={16} />
                    <Typography variant="body1">{val?.question}</Typography>
                  </Stack>
                ))
              }
              {
                Object.entries(advisorReview.questions_of_extra_details ?? {}).map(([key, val]) => (
                  <Stack
                    key={key}
                    direction="row"
                    alignItems="center"
                    gap={1}
                  >
                    <Icon name="bold_tag_verify" color={theme.palette.primary.main} size={16} />
                    <Typography variant="body1">{val?.question}</Typography>
                  </Stack>
                ))
              }
            </Stack>

          <InvestigationBlockInfo
            label={t("General.additional_advisor_comment")}
            text={advisorReview.comment}
            labelColor={theme.palette.ethicsBlue.main}
            textEmpty={t("General.no_information")}
            size={12}
            sx={{ mb: 1.5 }}
          />
        </>
      )}
    </>
  );
};

const CapturedInformation = ({ reportDetail }) => {
  const { lang } = useSelector((state) => state.lang);
  ////////// Obtaining report form label //////////
  const getFormLabel = (label, lang, default_value = "") => {
    if (typeof label === "string") {
      return label;
    } else {
      if (lang in label) {
        return label[lang];
      } else {
        return default_value;
      }
    }
  };
  return (
    <Stack spacing={2} paddingX={0.8} paddingY={1}>
      {reportDetail?.stepers?.map((item, index) => {
        if (index === 1) return null;
        return (
          // Section container
          <Box key={index}>
            {/* Section title */}
            <Box marginBottom={1.5}>
              <IconHeader
                key={index}
                iconName={
                  index === 0
                    ? "personal_id"
                    : index === 1
                    ? "outline_location"
                    : index === 2
                    ? "outline_note_search"
                    : "outline_direct_normal"
                }
                title={
                  item.locales ? getFormLabel(item.locales, lang) : item.title
                }
              />
            </Box>
            {/* Section Content */}
            <Grid2 container spacing={2} alignItems="center" padding={0}>
              {item?.form?.map((input, idx) => {
                let label =
                  input.locale != undefined && input.locale[lang]
                    ? input.locale[lang]
                    : input.label;
                let value =
                  input.locales_value != null && input.locales_value[lang]
                    ? input.locales_value[lang]
                    : input.text_value;

                if (input.key === "subject" || input.key === "description")
                  return null;
                if (typeof value === "boolean") return null;
                return <Input key={idx} label={label} value={value} />;
              })}
            </Grid2>
          </Box>
        );
      })}
    </Stack>
  );
};

const Input = ({ label, value }) => {
  const { lang } = useSelector((state) => state.lang);
  const theme = useTheme();

  if (Array.isArray(value)) return null;

  return (
    <>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Stack>
          {/* Question */}
          <Typography
            fontWeight={600}
            component="div"
            color="secondary"
            fontSize={12}
          >
            {label}&nbsp;
          </Typography>
          {/* Answer */}
          <Typography
            variant="subtitle2"
            component="div"
            color={theme.palette.oxfordBlue.main}
            borderBottom={`1px solid ${theme.palette.blueGray100.main}`}
            fontSize={16}
          >
            {value ?? "-----"}
          </Typography>
        </Stack>
      </Grid2>
    </>
  );
};

const DataFromReport = ({
  reportDetail,
  investigation,
  catalogCompanyRelationship,
  grievanceClassificationCat,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  // ---> Investigation keys <--- //
  const { general } = investigation;
  // ---> Item selected from the catalogs <--- //
  // complainant relatonship company
  const relationshipCompany =
    catalogCompanyRelationship?.find(
      (item) => item.value === general?.reporter?.relationship
    )?.label || t("General.unspecified");
  // Grievance Classification
  const grievanceClassification =
    grievanceClassificationCat?.find(
      (item) => item.value === general?.classification
    )?.label || t("General.unspecified");

  // ---> Entryway type <--- //
  const entryway = (entry) => {
    const entryMappings = {
      phone: t("entryways.phone"),
      email: t("entryways.email"),
      web: t("entryways.web"),
      whatsapp: t("entryways.whatsapp"),
      chat: t("entryways.chat"),
      app: t("entryways.app"),
      other: t("entryways.other"),
    };
    return entryMappings[entry] || "";
  };

  // Complainant Section Content
  const complainantField = [
    {
      question: t("General.name"),
      answer: general?.reporter?.name || t("General.no_name"),
    },
    {
      question: t("General.business_relationship"),
      answer: relationshipCompany,
    },
    {
      question: t("General.entryway"),
      answer: entryway(general?.reporter?.entryway) || t("General.unspecified"),
    },
    {
      question: t("General.range_date"),
      answer:
        general?.about?.event_happen?.from || general?.about?.event_happen?.to
          ? `${
              validInitDates(general?.about?.event_happen?.from)
                ? parseDateToDDMMYYYY2(general?.about?.event_happen?.from)
                : t("General.no_date")
            } ${t("General.at")} 
                ${
                  validInitDates(general?.about?.event_happen?.to)
                    ? parseDateToDDMMYYYY2(general?.about?.event_happen?.to)
                    : t("General.no_date")
                }`
          : t("General.unspecified"),
    },
    {
      question: t("General.classification"),
      answer: grievanceClassification,
    },
  ];

  return (
    <>
      <IconHeader
        iconName="personal_id"
        title={t("Datos de quien informa")}
        uppercase={false}
      />

      <Grid2 container spacing={2} py={1.5}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <InvestigationBlockInfo
            label={t("Relación con la empresa")}
            text={general?.reporter?.name}
            textEmpty={t("Sin especificar")}
            size={12}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <InvestigationBlockInfo
            label={t("Relación tiene con el caso informado")}
            text={general?.reporter?.name}
            textEmpty={t("Sin especificar")}
            size={12}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <InvestigationBlockInfo
            label={t("Nombre completo")}
            text={general?.reporter?.name}
            textEmpty={t("Sin especificar")}
            size={12}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <InvestigationBlockInfo
            label={t("Correo electrónico")}
            text={general?.reporter?.name}
            textEmpty={t("Sin especificar")}
            size={12}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 4 }}>
          <InvestigationBlockInfo
            label={t("Puesto")}
            text={general?.reporter?.name}
            textEmpty={t("Sin especificar")}
            size={12}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 4 }}>
          <InvestigationBlockInfo
            label={t("Teléfono fijo o móvil")}
            text={general?.reporter?.name}
            textEmpty={t("Sin especificar")}
            size={12}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 4 }}>
          <InvestigationBlockInfo
            label={t("Número de empleado")}
            text={general?.reporter?.name}
            textEmpty={t("Sin especificar")}
            size={12}
          />
        </Grid2>
      </Grid2>

      <IconHeader
        iconName="outline_location"
        title={t("Lugar y fecha en la que ocurrió la situación")}
        uppercase={false}
      />

      <Grid2 container spacing={2} py={1.5}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <InvestigationBlockInfo
            label={t("Centro de trabajo")}
            text={general?.reporter?.name}
            textEmpty={t("Sin especificar")}
            size={12}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <InvestigationBlockInfo
            label={t("Fecha exacta y hora")}
            text={general?.reporter?.name}
            textEmpty={t("Sin especificar")}
            size={12}
          />
        </Grid2>

        <Grid2 size={12}>
          <InvestigationBlockInfo
            label={t(
              "Fuera del centro de trabajo / Dentro del centro de trabajo"
            )}
            text={general?.reporter?.name}
            textEmpty={t("Sin especificar")}
            size={12}
          />
        </Grid2>
      </Grid2>

      <IconHeader
        iconName="outline_note_search"
        title={t("Detalle del caso")}
        uppercase={false}
      />

      <Grid2 container spacing={2} py={1.5}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <InvestigationBlockInfo
            label={t("Tipo de Reporte")}
            text={general?.reporter?.name}
            textEmpty={t("Sin especificar")}
            size={12}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <InvestigationBlockInfo
            label={t("Clasificación")}
            text={general?.reporter?.name}
            textEmpty={t("Sin especificar")}
            size={12}
          />
        </Grid2>

        {/* <Grid2 size={{ xs: 12, md: 6 }}>
          <InvestigationBlockInfo
            label={t("¿El fraude fue monetario o en especie?")}
            text={general?.reporter?.name}
            textEmpty={t("Sin especificar")}
            size={12}
          />
        </Grid2> */}
      </Grid2>
    </>
  );
};
